import { buttonVariants, cn, Container } from '@resolute/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Heart, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';


import { Logo } from './logo';
import { MobileNav } from './mobile-nav';
import { NavDropdown } from './nav-dropdown';
import { SIMPLE_NAV_ITEMS } from './nav-menu';
import { NavSimpleDropdown } from './nav-simple-dropdown';
import { highlightTransition, linkClass } from './nav-styles';
import { useNavMenus } from './use-nav-menus';

import { whatsappHref } from '@/features/contact/contact-details';
import { useWishlist } from '@/features/engagement/wishlist-context';
import { PANEL_TRANSITION } from '@/lib/motion';
import { useHoverHighlight } from '@/lib/use-hover-highlight';

/** Scroll past this (roughly the header's own height) before it may retract —
 *  hiding it within the first few pixels reads as a glitch. */
const RETRACT_AFTER_PX = 80;
/** Minimum movement treated as a real direction change, so sub-pixel jitter and
 *  elastic overscroll don't flip the bar back and forth. */
const SCROLL_DELTA_THRESHOLD = 6;

export function Header() {
  const reduce = useReducedMotion();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [retracted, setRetracted] = useState(false);
  const { count: wishlistCount } = useWishlist();
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const lastScrollY = useRef(0);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const onScroll = () => {
      const y = Math.max(0, window.scrollY);
      setScrolled(y > 8);

      const delta = y - lastScrollY.current;
      // Ignore sub-pixel jitter and the elastic overscroll at either end, which
      // otherwise flip the direction repeatedly and make the bar flicker.
      if (Math.abs(delta) < SCROLL_DELTA_THRESHOLD) return;
      lastScrollY.current = y;
      // Only retract once past the header's own height: hiding it during the
      // first few pixels of scroll reads as a glitch rather than an intent.
      setRetracted(delta > 0 && y > RETRACT_AFTER_PX);
    };
    lastScrollY.current = Math.max(0, window.scrollY);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Reset to visible on route change — a new page starts at the top, and the
  // scroll handler only re-evaluates on the next actual scroll event.
  useEffect(() => {
    setRetracted(false);
    lastScrollY.current = 0;
  }, [location.pathname, location.search]);

  // Close menus whenever the route changes.
  useEffect(() => {
    setOpenId(null);
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  // Close the desktop mega-menu on outside pointer / Escape.
  useEffect(() => {
    if (!openId) return;
    const onPointer = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [openId]);

  // Instant open/toggle — used only for deliberate actions (click, keyboard
  // focus). Always clears any pending timer from the hover paths below.
  const openMenu = (id: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openTimer.current) clearTimeout(openTimer.current);
    setOpenId(id);
  };
  const toggleMenu = (id: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openTimer.current) clearTimeout(openTimer.current);
    setOpenId((cur) => (cur === id ? null : id));
  };
  // Small hover-intent delay before opening on mouseenter. Without this, a
  // sticky header sliding under a STATIONARY cursor during scroll (the page
  // moves, the mouse doesn't) still fires a genuine mouseenter on whatever
  // trigger ends up underneath — opening a menu the user never intended to
  // hover. A brief delay, cancelled on mouseleave, filters out that fleeting
  // scroll-induced overlap while staying imperceptible for a real hover.
  const scheduleOpen = (id: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setOpenId(id), 120);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openTimer.current) clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setOpenId(null), 120);
  };

  const solid = scrolled || openId !== null;
  const navHighlight = useHoverHighlight<HTMLUListElement>();
  const menus = useNavMenus();

  /**
   * Slide the bar out of the way on the way down, bring it back on the way up.
   *
   * Suppressed whenever a menu is open — retracting would take the mega-menu
   * panel or the whole mobile sheet with it — and for `prefers-reduced-motion`,
   * where a bar that moves itself is exactly the kind of unrequested motion the
   * setting asks us to avoid, so it simply stays put.
   */
  const concealed = retracted && openId === null && !mobileOpen && !reduce;

  // Published to <html> so other sticky bars can follow the header up via the
  // `--header-offset` variable (see global.css) without prop-drilling or a
  // context that would re-render whole pages on every scroll direction change.
  useEffect(() => {
    document.documentElement.dataset.headerConcealed = String(concealed);
    return () => {
      delete document.documentElement.dataset.headerConcealed;
    };
  }, [concealed]);

  return (
    <header
      ref={navRef}
      className={cn(
        // `transition-colors` alone would not animate the slide — the transform
        // has to be named in the property list too.
        'sticky top-0 z-40 w-full border-b transition-[transform,color,background-color,border-color] duration-base',
        concealed && '-translate-y-full',
        solid
          ? 'border-border/70 bg-background/70 backdrop-blur-xl backdrop-saturate-150'
          : 'border-transparent bg-background/0',
      )}
    >
      <Container className="flex h-14 items-center justify-between gap-4 lg:h-16">
        <Logo />

        {/* Primary desktop navigation */}
        <nav aria-label="Primary" className="hidden lg:block">
          <ul
            ref={navHighlight.containerRef}
            className="relative flex items-center gap-1"
            onMouseLeave={() => {
              scheduleClose();
              navHighlight.clear();
            }}
          >
            {/* One persistent, gliding highlight shared by every top-level
                item — see useHoverHighlight for why this beats a per-item
                shared-layoutId approach (no mount/unmount flicker). */}
            <motion.span
              aria-hidden="true"
              style={{ pointerEvents: 'none' }}
              className="absolute rounded-md bg-muted"
              animate={
                navHighlight.rect ? { ...navHighlight.rect, opacity: 1 } : { opacity: 0 }
              }
              transition={highlightTransition(reduce)}
            />
            {menus.map((menu) => (
              <NavDropdown
                key={menu.id}
                menu={menu}
                isOpen={openId === menu.id}
                onOpen={() => openMenu(menu.id)}
                onHoverOpen={() => scheduleOpen(menu.id)}
                onToggle={() => toggleMenu(menu.id)}
                onCloseRequest={() => setOpenId(null)}
                onScheduleClose={scheduleClose}
                onMeasure={navHighlight.measure}
              />
            ))}

            {SIMPLE_NAV_ITEMS.map((item) =>
              item.type === 'link' ? (
                <li key={item.to} className="relative">
                  <NavLink
                    to={item.to}
                    className={linkClass}
                    onMouseEnter={(e) => navHighlight.measure(e.currentTarget)}
                    onFocus={(e) => navHighlight.measure(e.currentTarget)}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ) : (
                <NavSimpleDropdown
                  key={item.id}
                  dropdown={item}
                  isOpen={openId === item.id}
                  onOpen={() => openMenu(item.id)}
                  onHoverOpen={() => scheduleOpen(item.id)}
                  onToggle={() => toggleMenu(item.id)}
                  onCloseRequest={() => setOpenId(null)}
                  onScheduleClose={scheduleClose}
                  onMeasure={navHighlight.measure}
                />
              ),
            )}
          </ul>
        </nav>

        {/* Right-side actions */}
        <div className="flex items-center gap-2">
          <Link
            to="/wishlist"
            aria-label={`Shortlist${wishlistCount ? ` (${wishlistCount})` : ''}`}
            className="relative hidden size-10 items-center justify-center rounded text-foreground transition-colors duration-fast ease-out-expo hover:bg-muted sm:flex"
          >
            <Heart className="size-5" aria-hidden="true" />
            {wishlistCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex min-w-[1.15rem] items-center justify-center rounded bg-primary px-1 text-xs font-medium text-primary-foreground">
                {wishlistCount}
              </span>
            )}
          </Link>
          {/* Opens WhatsApp with the advisor number and a ready-to-send
              message — the same `whatsappHref()` deep link the contact page's
              WhatsApp card uses, so the number and copy stay in one place. */}
          <a
            href={whatsappHref()}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant: 'primary', size: 'sm' }), 'hidden sm:inline-flex')}
          >
            Talk to an advisor
          </a>

          <button
            type="button"
            className="flex size-10 items-center justify-center rounded text-foreground hover:bg-muted lg:hidden"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="size-6" aria-hidden="true" /> : <Menu className="size-6" aria-hidden="true" />}
          </button>
        </div>
      </Container>

      <AnimatePresence initial={false}>
        {mobileOpen && (
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={PANEL_TRANSITION}
          >
            <MobileNav onNavigate={() => setMobileOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
